import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, map, catchError, of } from 'rxjs';
import { User } from '../model/User';
import { CsrfService } from './csrf.service';
import { UserState } from '../store/User/UserReducer';
import {
  initUserSelector,
  initIsAuthenticatedSelector,
} from '../store/User/UserSelector';
import { SetCsrfToken } from '../store/User/UserActions';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(
    private readonly csrfService: CsrfService,
    private readonly store: Store<UserState>,
  ) {}

  isLoggedIn(): Observable<boolean> {
    return this.store.select(initIsAuthenticatedSelector).pipe(
      catchError((error) => {
        console.error('Error checking authentication status:', error);
        return of(false);
      }),
    );
  }

  getUser(): Observable<User | null> {
    return this.store.select(initUserSelector).pipe(
      catchError((error) => {
        console.error('Error getting user from store:', error);
        return of(null);
      }),
    );
  }

  getCurrentUser(): User | null {
    try {
      let currentUser: User | null = null;
      this.store
        .select(initUserSelector)
        .pipe(
          map((user) => (currentUser = user)),
          catchError((error) => {
            console.error('Error getting current user from store:', error);
            return of(null);
          }),
        )
        .subscribe()
        .unsubscribe();
      return currentUser;
    } catch (error) {
      console.error('Error accessing store for current user:', error);
      return null;
    }
  }

  isCurrentlyLoggedIn(): boolean {
    try {
      let isAuthenticated = false;
      this.store
        .select(initIsAuthenticatedSelector)
        .pipe(
          map((auth) => (isAuthenticated = auth)),
          catchError((error) => {
            console.error(
              'Error checking authentication status from store:',
              error,
            );
            return of(false);
          }),
        )
        .subscribe()
        .unsubscribe();
      return isAuthenticated;
    } catch (error) {
      console.error('Error accessing store for authentication status:', error);
      return false;
    }
  }

  async logout() {
    const { token: csrfToken } = await this.fetchCsrfToken();
    if (!csrfToken) {
      console.error('Failed to get CSRF token for logout');
      return;
    }
    const headers = {
      'X-XSRF-TOKEN': csrfToken,
      'Content-Type': 'application/x-www-form-urlencoded',
    };

    const resp = await fetch('/cw/api/j_spring_security_logout', {
      method: 'POST',
      credentials: 'include',
      headers,
    });

    console.debug(`Logout response: ${JSON.stringify(resp)}`);
  }

  async fetchCsrfToken() {
    const url = '/cw/api/csrf';
    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        const text = await response.text();
        console.error('Non-OK response:', text);
        throw new Error(`HTTP ${response.status}: ${text}`);
      }
      if (!response.headers.get('Content-Type')?.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Expected JSON, received HTML');
      }
      const data = (await response.json()) as {
        parameterName: string;
        token: string;
      };
      console.debug(`fetchCsrfToken :: ${JSON.stringify(data)}`);
      if (data.parameterName && data.token) {
        this.store.dispatch(new SetCsrfToken(data.parameterName, data.token));
        return { param: data.parameterName, token: data.token };
      }
      console.warn('CSRF token not found in /cw/api/csrf response');
      return null;
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
      return null;
    }
  }

  jLoginPromise(
    username: string,
    password: string,
    csrfToken: string,
  ): Promise<any> {
    const body = new URLSearchParams({
      j_username: username,
      j_password: password,
      login: '',
    });

    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Requested-With': 'XMLHttpRequest',
      'X-XSRF-TOKEN': csrfToken,
    };

    return fetch('/cw/api/j_spring_security_check', {
      method: 'POST',
      credentials: 'include',
      headers: headers,
      body: body.toString(),
    });
  }

  userInfo() {
    return this.csrfService.fetchWithCsrfGet('/cw/api/userinfo');
  }
}
